import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSidebar } from "@services/SidebarContext";
import SubTitleHeader from "@shared/components/SubTitleHeader";
import ProjectForm from "@pages/Project/components/ProjectForm";
import useEmojiPicker from "@pages/Project/utils/useEmojiPicker";
import { useForm, FormProvider } from "react-hook-form";
import UnsavedChangesModal from "@shared/components/UnsavedChangesPopup/UnsavedChangesModal";
import { useUnsavedChangesWarning } from "@shared/components/UnsavedChangesPopup/useUnsavedChangesWarning";
import { useProject } from "@utils/useProject";
import { FeatureCollection } from "@shared/components/Mapbox/utils/interfaces";
import {
  CreateProjectPageProps,
  ProjecFormDataProps,
} from "@shared/interfaces/ProjectsFiles";
import { toastService } from "@services/ToastService";
import NewItemButton from "@shared/components/NewItemButton";

const CreateProject: React.FC<CreateProjectPageProps> = ({ onNext }) => {
  const [submitting, setSubmitting] = useState(false);
  const [geoJson, setGeoJson] = useState<any>(null);
  const methods = useForm({
    mode: "all", //  Show errors immediately when the user types
    reValidateMode: "onChange",
    criteriaMode: "all",
    defaultValues: {
      name: "",
      description: "",
      icon: "",
      // countryId: 0,
      countryName: "",
      startDate: "",
      endDate: "",
      funders: "",
      formattedBudget: "",
      geojson_data: null as FeatureCollection | null,
      countryIds: [] as number[],
      currencyId: undefined as unknown as number,
    },
  });

  const {
    handleSubmit,
    setValue,
    getValues,
    formState: { isValid, isDirty }, //  Extract isValid
  } = methods;

  const [icon, setIcon] = useState("");
  const {
    icon: selectedIcon,
    showEmojiPicker,
    setShowEmojiPicker,
    handleEmojiSelect,
  } = useEmojiPicker(icon, setIcon);

  const { createProject, loading } = useProject();

  useEffect(() => {
    if (geoJson) {
      handleGeoJsonUpdate(geoJson);
    }
  }, [geoJson]);

  const { refreshProjects } = useSidebar();

  const handleGeoJsonUpdate = (updatedGeoJson: FeatureCollection) => {
    setValue("geojson_data", updatedGeoJson);
    setGeoJson(updatedGeoJson);
  };

  const onSubmit = async (values: ProjecFormDataProps) => {
    setSubmitting(true);
    try {
      const data = {
        name: values.name,
        description: values.description,
        icon: values.icon,
        start_date: values.startDate,
        end_date: values.endDate,
        funders: values.funders,
        budget: `${Number(values.formattedBudget.replace(/[^\d]/g, ""))}`,
        country_ids: values.countryIds,
        geojson_data: values.geojson_data,
        currency_id: values.currencyId,
      };

      const response = await createProject(data);

      if (response?.id) {
        refreshProjects();

        methods.reset(methods.getValues());
        setTimeout(() => {
          onNext(response.id);
        }, 500);
      } else {
        toastService.error("Project creation failed. Please try again.");
      }
    } catch (error) {
      console.error("Error creating project:", error);
      toastService.error(
        "An unexpected error occurred while creating the project."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // blocker is a function that will block navigation if there are unsaved changes
  const { blocker, handleProceed, handleCancel, isBlocked } =
    useUnsavedChangesWarning(
      getValues(), // current form values
      methods.control._defaultValues // initial/default values
    );

  return (
    <FormProvider {...methods}>
      <UnsavedChangesModal
        onProceed={handleProceed}
        onCancel={handleCancel}
        isOpen={isBlocked}
      />
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full max-h-screen flex flex-col relative"
      >
        <div className="px-8">
          <SubTitleHeader
            spacer={true}
            title="Create a project"
            children={
              <NewItemButton
                showIcon={false}
                type="submit"
                title="Create"
                disabled={!isValid || submitting}
                isLoading={submitting}
              />
            }
          />
        </div>

        {/* Scrollable Content */}
        <div className="p-8 flex-1 ">
          {" "}
          {/* Extra padding at bottom to avoid overlap */}
          <ProjectForm
            isEditing={true}
            showEmojiPicker={showEmojiPicker}
            setShowEmojiPicker={setShowEmojiPicker}
            icon={selectedIcon}
            handleEmojiSelect={(emoji) => {
              setValue("icon", emoji.emoji), handleEmojiSelect(emoji);
            }}
            handleGeoJsonUpdate={handleGeoJsonUpdate}
            createForm={true}
            loading={loading}
          />
        </div>
      </form>
    </FormProvider>
  );
};

const MainComponent: React.FC = () => {
  const navigate = useNavigate();

  const nextStage = (id?: string) => {
    if (id) {
      navigate(`/project/${id}?tab=Documents`);
    }
  };

  return (
    <div className="flex justify-center items-start w-full h-screen">
      <div className="w-full">
        <CreateProject onNext={nextStage} />
      </div>
    </div>
  );
};

export default MainComponent;
